import React, { Fragment, useState, useRef, useCallback } from "react";
import { ReactComponent as SearchIcon } from "../../asset/iconamoon_search-light.svg";
import useFetchRiderAssignmentById from "../Libs/useFetchRiderAssignmentById";
import { ReactComponent as LocaIcon } from "../../asset/fluent_location-20-regular.svg";
import { ReactComponent as BoxIcon } from "../../asset/box-tick-svgrepo-com 1.svg";
import useAcceptPackageByRider from "../Libs/useAcceptPackageByRider";
import useRejectPackageByRider from "../Libs/useRejectPackageByRider";
import Spinner from "../../utils/spinner";
import "./Riderhome.css";

function Riderhome() {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useFetchRiderAssignmentById();

  const assignments =
    data?.pages?.flatMap((page) => page.riderAssignments) || [];

  const acceptPackageMutation = useAcceptPackageByRider();
  const rejectPackageMutation = useRejectPackageByRider();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectPkgId, setSelectedPkgId] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [acceptOverlay, setAcceptOverlay] = useState(false);
  const [acceptLoading, setAccepLoading] = useState(false);
  const [accptError, setAcceptError] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [rejectOverlay, setRejectOverlay] = useState(false);

  const filteredAssignments = assignments
    .filter((assignment) => {
      const receiver = assignment?.package?.[0]?.receiver || {};
      const name = assignment?.package?.[0]?.name || "";
      const state = receiver?.state || "";
      const address = receiver?.address_1 || "";
      const receiverName = receiver?.name || "";

      const query = searchQuery.toLowerCase();

      return (
        state.toLowerCase().includes(query) ||
        address.toLowerCase().includes(query) ||
        name.toLowerCase().includes(query) ||
        receiverName.toLowerCase().includes(query)
      );
    })
    .reverse();

  const observerRef = useRef();
  const lastRiderAssignmentElementRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  const handleAcceptPackage = async () => {
    setAccepLoading(true);
    try {
      await acceptPackageMutation.mutateAsync(selectPkgId);
    } catch (error) {
      setAcceptError(error.message);
    } finally {
      setSelectedPkgId(null);
      setAcceptOverlay(false);
      setAccepLoading(false);
    }
  };

  const handleRejectPackage = async () => {
    setRejectLoading(true);
    try {
      await rejectPackageMutation.mutateAsync(assignmentId);
    } catch (error) {
      setRejectError(error.message);
    } finally {
      setAssignmentId(null);
      setRejectOverlay(false);
      setRejectLoading(false);
    }
  };

  return (
    <Fragment>
      {acceptOverlay && (
        <div className="accept-over-con">
          <div className="card">
            <div className="box">
              <BoxIcon />
            </div>
            <h3>Accept Trip</h3>
            <p>Do you want to accept this Trip?</p>
            <div className="btn">
              <button
                onClick={() => {
                  setSelectedPkgId(null);
                  setAcceptOverlay(false);
                }}
              >
                No
              </button>
              <button onClick={handleAcceptPackage} disabled={acceptLoading}>
                {acceptLoading ? "Accepting..." : "Yes"}
              </button>
            </div>
            {accptError && <p>{accptError}</p>}
          </div>
        </div>
      )}

      {rejectOverlay && (
        <div className="accept-over-con">
          <div className="card">
            <div className="box">
              <BoxIcon />
            </div>
            <h3>Reject Trip</h3>
            <p>Do you want to reject this Trip?</p>
            <div className="btn">
              <button
                onClick={() => {
                  setAssignmentId(null);
                  setRejectOverlay(false);
                }}
              >
                No
              </button>
              <button onClick={handleRejectPackage} disabled={rejectLoading}>
                {rejectLoading ? "Rejecting..." : "Yes"}
              </button>
            </div>
            {rejectError && <p>{rejectError}</p>}
          </div>
        </div>
      )}

      <div className="rider-home">
        <div className="my-assignment">
          <span>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search Trips by receiver name, address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </span>

          <div className="assignment">
            {isLoading && <Spinner />}
            {isError && <p>{error.message}</p>}

            {!isLoading && !isError && filteredAssignments.length === 0 && (
              <p style={{ fontFamily: "Montserrat" }}>
                No matching assignments found.
              </p>
            )}

            {!isLoading &&
              !isError &&
              filteredAssignments.map((assignment, index) => {
                const isLast = index === filteredAssignments.length - 1;
                return (
                  <div
                    key={assignment._id}
                    className="assignment-card"
                    ref={isLast ? lastRiderAssignmentElementRef : null}
                  >
                    <h2>ID {assignment?._id.slice(0, 8).toUpperCase()}</h2>
                    <p>
                      {new Date(
                        assignment?.package?.[0]?.dateCreated
                      ).toLocaleString()}
                    </p>
                    <div className="in">
                      <span>
                        {assignment?.package?.[0]?.items?.map((item) => (
                          <div key={item?._id}>
                            {item?.name} - {item?.quantity}
                          </div>
                        )) || "N/A"}
                      </span>
                    </div>
                    <div className="inner">
                      <p>
                        <LocaIcon />
                        {`${assignment?.package?.[0]?.receiver?.state || ""}, ${
                          assignment?.package?.[0]?.receiver?.address_1 || ""
                        }`}
                      </p>
                      <span>
                        <p>Receiver Name:</p>
                        <h3>
                          {assignment?.package?.[0]?.receiver?.name || "N/A"}
                        </h3>
                      </span>
                      <span>
                        <p>Receiver Phone Number:</p>
                        <h3>
                          {assignment?.package?.[0]?.receiver?.phone_number ||
                            "N/A"}
                        </h3>
                      </span>
                    </div>
                    <p>
                      Accepted: {assignment?.accept?.length > 0 ? "Yes" : "No"}
                    </p>
                    <div className="btn">
                      <button
                        onClick={() => {
                          setAssignmentId(assignment._id);
                          setRejectOverlay(true);
                        }}
                        disabled={assignment?.accept?.length > 0}
                      >
                        Reject
                      </button>

                      <button
                        onClick={() => {
                          setSelectedPkgId(assignment?.package?.[0]?._id);
                          setAcceptOverlay(true);
                        }}
                        disabled={assignment?.accept?.length > 0}
                      >
                        Accept
                      </button>
                    </div>
                  </div>
                );
              })}

            {isFetchingNextPage && <Spinner />}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default Riderhome;
